import { SOCKET_EVENT } from './types';
import type {
  ISocketService,
  MessageData,
  SocketWorkerMessage,
  WorkerMessage,
} from './types';

export class SocketService implements ISocketService {
  private _worker: SharedWorker | null = null;
  private _isConnected = false;
  private _port: MessagePort | null = null;
  private _listeners = new Map<string, Set<(data: unknown) => void>>();
  private _ackListeners = new Map<string, () => void>();
  private static instance: SocketService;

  private constructor() {}

  public static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }

    return SocketService.instance;
  }

  public initialize(): void {
    if (this._worker) return;

    try {
      this._worker = new SharedWorker(
        new URL('./workers/socket-io.shared.worker.ts', import.meta.url),
        { type: 'module' },
      );

      this._worker.onerror = (error) => {
        console.error('[SocketService] SharedWorker onerror:', error);
      };

      this._port = this._worker.port;

      // 워커로부터 broadcast 된 message를 받아 실행 한다.
      this._port.onmessage = (event: MessageEvent<SocketWorkerMessage>) => {
        this.handleBroadcast(event.data);
      };
      this._port.onmessageerror = (error) => {
        console.error('Worker port error:', error);
      };

      // 메인 스레드에서 포트를 시작해야 워커에서 보낸 메시지(CONNECT 등)를 수신할 수 있다.
      this._port.start();
    } catch (error) {
      console.error('Worker creation failed:', error);
    }
  }

  public isConnected(): boolean {
    return this._isConnected;
  }

  private handleBroadcast(message: SocketWorkerMessage): void {
    const { type, data } = message;

    if (type === SOCKET_EVENT.SEND_OK && data?.uid) {
      const callback = this._ackListeners.get(data.uid);
      if (callback) {
        callback();
        this._ackListeners.delete(data.uid);
      }
      return;
    }

    if (type === SOCKET_EVENT.CONNECT) {
      this._isConnected = true;
    } else if (type === SOCKET_EVENT.DISCONNECT) {
      this._isConnected = false;
    }

    this.executeCallback(type, data);
  }

  private executeCallback(
    eventName: string,
    data: MessageData | undefined,
  ): void {
    this._listeners.get(eventName)?.forEach((callback) => callback(data));
  }

  public on(eventName: string, callback: (data: unknown) => void): void {
    if (!this._listeners.has(eventName)) {
      this._listeners.set(eventName, new Set());
    }
    this._listeners.get(eventName)?.add(callback);

    if (eventName === SOCKET_EVENT.CONNECT && this._isConnected) {
      callback(undefined);
    }
  }

  public off(eventName: string, callback: (data: unknown) => void): void {
    this._listeners.get(eventName)?.delete(callback);
  }

  private sendMessage(message: WorkerMessage): void {
    this._port?.postMessage(message);
  }

  public connectSocket(): void {
    this.sendMessage({ type: 'connect' });
  }

  public disconnectSocket(): void {
    this.sendMessage({ type: 'disconnect' });
  }

  public send(event: string, data?: MessageData, callback?: () => void): void {
    if (!this._isConnected) {
      console.warn('Socket not connected. Message dropped:', event, data);
      return;
    }

    if (callback && data?.uid) {
      this._ackListeners.set(data.uid, callback);
    }

    this.sendMessage({ type: 'send', event, data });
  }
}
